import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Email,
  EmailDirection,
  deleteEmail,
  fetchIncomingEmails,
  fetchIncomingEmailsByEmail,
  fetchOutgoingEmails,
  fetchOutgoingEmailsByEmail,
} from '../../services/emailService';
import '../../styles/AccountTools.css';

type Message = { type: 'success' | 'error'; text: string } | null;

const Inbox = () => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const userEmail = user?.email || user?.contactDetails?.personalEmail || user?.contactDetails?.email || '';
  const [activeTab, setActiveTab] = useState<EmailDirection>('INBOUND');
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<Message>(null);

  const loadEmails = useCallback(async () => {
    if (!userId && !userEmail) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const page =
        activeTab === 'INBOUND'
          ? userId
            ? await fetchIncomingEmails(userId, 0, 50)
            : await fetchIncomingEmailsByEmail(userEmail, 0, 50)
          : userId
            ? await fetchOutgoingEmails(userId, 0, 50)
            : await fetchOutgoingEmailsByEmail(userEmail, 0, 50);
      setEmails(page.content || []);
      setMessage(null);
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to load emails.' });
    } finally {
      setLoading(false);
    }
  }, [activeTab, userEmail, userId]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const handleDelete = async (emailId: string) => {
    if (!userId) {
      setMessage({ type: 'error', text: 'Your profile id is still loading. Refresh, then try deleting again.' });
      return;
    }

    if (!window.confirm('Delete this email?')) return;

    try {
      await deleteEmail(emailId, userId);
      setEmails((prev) => prev.filter((email) => email.id !== emailId));
      setMessage({ type: 'success', text: 'Email deleted.' });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to delete email.' });
    }
  };

  if (!userId && !userEmail) {
    return (
      <main className="tool-page">
        <div className="tool-card tool-empty">Sign in to view your inbox.</div>
      </main>
    );
  }

  return (
    <main className="tool-page">
      <div className="tool-header">
        <div>
          <h1>Emails</h1>
          <p>Inbox and sent mail from the same email-service endpoints used by mobile.</p>
        </div>
        <div className="tool-actions">
          <button className="tool-btn-secondary" type="button" onClick={loadEmails} disabled={loading}>Refresh</button>
        </div>
      </div>

      {message && <div className={`tool-message ${message.type}`}>{message.text}</div>}

      <div className="tool-tabs">
        <button className={activeTab === 'INBOUND' ? 'active' : ''} type="button" onClick={() => setActiveTab('INBOUND')}>
          Inbox
        </button>
        <button className={activeTab === 'OUTBOUND' ? 'active' : ''} type="button" onClick={() => setActiveTab('OUTBOUND')}>
          Sent
        </button>
      </div>

      <section className="tool-card">
        {loading ? (
          <div className="tool-loading">Loading emails...</div>
        ) : emails.length === 0 ? (
          <div className="tool-empty">{activeTab === 'INBOUND' ? 'No incoming emails yet.' : 'No sent emails yet.'}</div>
        ) : (
          <div className="tool-list">
            {emails.map((email) => {
              const displayName =
                activeTab === 'INBOUND'
                  ? email.sender?.email || 'Unknown sender'
                  : email.recipients?.[0]?.email || 'Unknown recipient';
              return (
                <div className="tool-row" key={email.id}>
                  <Link className="tool-row-main" to={`/account/inbox/${email.id}`}>
                    <div className="tool-row-title">
                      {String(email.status || '').toUpperCase() === 'UNREAD' ? <span className="tool-pill">Unread</span> : null}
                      {' '}
                      {displayName}
                    </div>
                    <div className="tool-row-sub">{email.title || 'No subject'}</div>
                    <div className="tool-row-sub">
                      {email.timestamp ? new Date(email.timestamp).toLocaleString() : 'No timestamp'}
                      {(email.attachments || []).length > 0 ? ` - ${email.attachments?.length} attachment(s)` : ''}
                    </div>
                  </Link>
                  <button className="tool-btn-danger" type="button" onClick={() => handleDelete(email.id)}>
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

    </main>
  );
};

export default Inbox;
